import moment from 'moment';
import { useSearchParams } from 'react-router-dom';

export const validateFilterNumber = (values) => {
    let numbers = [];
    if (Array.isArray(values)) {
        values.forEach((value) => {
            value = Number(value);
            if (!isNaN(value)) numbers.push(value);
        });
    }
    return numbers.length > 0 ? numbers : undefined;
};

export const validateDate = (dateValue, { cutoffDate = true } = {}) => {
    // Only expects YYYY-MM-DD format (ISO)
    let timeStamp = Date.parse(dateValue);
    if (isNaN(timeStamp)) return;
    let date = new Date(timeStamp);
    if (cutoffDate) date = date < new Date() ? date : undefined;
    return date;
};

export const getDateWithISOFormat = (date) => {
    let isValidDate = (date) => date && moment(date).isValid();
    if (isValidDate(date)) return moment(date).format('YYYY-MM-DD');
    return null;
};


const getRawQueryParam = (key, search = window.location.search) => {
    const params = new URLSearchParams(search);
    const rawParams = search.replace(/^\?/, '');
    const matched = rawParams.match(new RegExp(`${key}=([^&]+)`));
    return matched ? decodeURIComponent(matched[1]) : params.get(key);
};


// `search` lets a table that keeps its filters out of the URL read them from its own query string instead.
export const readFilterQueryParams = (filterSchema, query, search) => {
    let queryObject = {};
    let filterStatus = {};
    const localParams = typeof search === 'string' ? new URLSearchParams(search) : null;

    const getQuery = (key) => {
        return localParams ? localParams.get(key) : query.get(key);
    };

    Object.values(filterSchema).forEach((filterObject) => {
        const { queryParamKey, filterName } = filterObject;
        let currentQuery = {};

        if (typeof queryParamKey === 'object') {
            const { keyList } = queryParamKey;
            keyList.forEach((key) => {
                let queryValue = getQuery(key);
                if (queryValue) Object.assign(currentQuery, { [key]: queryValue });
            });
        } else {
            let rawValue = localParams ? getRawQueryParam(queryParamKey, search) : getRawQueryParam(queryParamKey);
            if (rawValue) {
                let values = rawValue.split('^');
                currentQuery = { [queryParamKey]: values };
            }
        }

        if (filterObject.parse && typeof filterObject.parse === 'function') {
            currentQuery = filterObject.parse(currentQuery);
        }

        filterStatus = Object.assign(filterStatus, { [filterName]: Object.keys(currentQuery).length > 0 });
        Object.assign(queryObject, currentQuery);
    });
    return { queryObject, filterStatus };
};

export const transformQueryFilters = (filterSchema, queryObject) => {
    let filterPayload = {};
    Object.values(filterSchema).forEach(({ submitKey, queryParamKey }) => {
        let currentPayload = {};
        if (typeof queryParamKey === 'object') {
            let currentQuery = {};
            queryParamKey.keyList.forEach((key) => {
                if (queryObject[key]) {
                    Object.assign(currentQuery, { [key]: queryParamKey[queryObject] });
                }
            });
            if (Object.keys(currentQuery).length > 0) {
                currentPayload = submitKey(queryObject);
            }
        } else {
            if (typeof submitKey === 'function') currentPayload = submitKey(queryObject);
            else {
                let value = queryObject?.[queryParamKey];
                if (value) currentPayload = { [submitKey]: value };
            }
        }
        if (Object.keys(currentPayload).length > 0) Object.assign(filterPayload, currentPayload);
    });
    return filterPayload;
};

export const getFilterQueryParams = (filterSchema, filterList) => {
    let filterSearchQueryParam = ``;
    Object.values(filterSchema).forEach((filterObject) => {
        const { queryParamKey, filterName } = filterObject;
        if (typeof queryParamKey === 'object') {
            let currentFilter = filterList[filterName];
            if (currentFilter) {
                let queryObject = queryParamKey.getQueryObject(currentFilter);
                const isBothAmountZero = filterObject.type === 'amount' && queryObject.min === 0 && queryObject.max === 0;
                if (isBothAmountZero) {
                    return;
                }
                Object.entries(queryObject).forEach(([queryKey, queryValue]) => {
                    if (![undefined, null, ''].includes(queryValue)) {
                        if (filterSearchQueryParam !== '') {
                            filterSearchQueryParam += '&';
                        }
                        filterSearchQueryParam += queryKey + '=' + queryValue;
                    }
                });
            }
        } else {
            let currentFilter = filterList[filterName] || [];
            let selectedFilters = [];
            if (Array.isArray(currentFilter)) {
                selectedFilters = currentFilter.filter((filt) => filt.isSelected);
            }

            let isSelected = selectedFilters.length !== currentFilter.length;
            let selectedFilterValues = selectedFilters.map((filx) => filx.apiValue);

            if (isSelected && selectedFilters.length > 0) {
                if (filterSearchQueryParam !== '') {
                    filterSearchQueryParam += '&';
                }
                filterSearchQueryParam += queryParamKey + '=' + selectedFilterValues.join('^');
            }
        }
    });
    return filterSearchQueryParam;
};

export const updateQueryParams = ({
    destinationTab,
    mainTab,
    limit,
    page,
    query,
    filterList = {},
    filterSchema,
    defaultRowsPerPage,
    defaultPage,
    navigate,
    archive,
    warehouseId,
    siteId,
    subTab,
    client_search,
    view
} = {}) => {
    if (!limit || isNaN(limit)) limit = defaultRowsPerPage;
    if (!page || isNaN(page)) page = defaultPage;
    let search = '';
    if (mainTab) search = `?mainTab=${mainTab}`;
    if (destinationTab) {
        if (search.length > 0) search += `&tab=${destinationTab}`;
        else search = `?tab=${destinationTab}`;
    }
    if (search.length > 0) search += `&page=${page}`;
    else search = `?page=${page}`;
    if (limit) search = search + `&limit=${limit}`;
    if (query) search = search + `&query=${query}`;
    if (archive) search = search + `&archive=${true}`;
    if (warehouseId) search = search + `&selectedWarehouseId=${warehouseId}`;
    if (siteId) search = search + `&selectedSiteId=${siteId}`;
    if (subTab) search = search + `&subTab=${subTab}`
    if (client_search) search = search + `&client_search=${client_search}`;
    if (view) search = search + `&view=${view}`;
    if (filterSchema && filterList) {
        let filterQuery = getFilterQueryParams(filterSchema, filterList);
        if (filterQuery) search = search + `&${filterQuery}`;
    }
    navigate(`${window.location.pathname}${search}`);
};

export const updateSearchTermQuery = ({ searchTerm, limit, navigate, removeTheseKeys = [] }) => {
    let searchParams = new URLSearchParams(window.location.search);
    if (removeTheseKeys?.length > 0) {
        removeTheseKeys.forEach(key => searchParams.delete(key));
    }
    if (searchTerm) searchParams.set('query', searchTerm);
    else searchParams.delete('query');
    searchParams.set('page', 1);
    if (limit) searchParams.set('limit', limit);
    searchParams = searchParams.toString();
    navigate({
        pathname: window.location.pathname,
        search: searchParams,
    });
};



export const validateFilterString = (values) => {
    let list = [];
    if (Array.isArray(values)) {
        values.forEach((value) => {
            if (String(value)) list.push(value);
        });
    }
    return list.length > 0 ? list : undefined;
};



export const updateClientSearchTermQuery = ({ searchClient, setSearchParams }) => {
    setSearchParams(params => {
        if (searchClient) {
            params.set('client_search', searchClient);
        } else {
            params.delete('client_search');
        }
        return params;
    });
};


export function splitArrayElements(arr) {
    if (!Array.isArray(arr)) {
        return undefined
    }

    return arr.flatMap((item, index) => {
        if (typeof item !== 'string') {
            return undefined
        }

        const parts = item.split('+').map(str => str.trim());

        if (parts.some(p => !p)) {
            return undefined
        }

        return parts;
    });
}




export const updateQueryParamsForTickets = ({
    destinationTab,
    mainTab,
    limit,
    page,
    query,
    filterList = {},
    filterSchema,
    defaultRowsPerPage,
    defaultPage,
    navigate,
    archive,
    warehouseId,
    siteId,
    subTab,
    client_search,
    additionalParams = {}
} = {}) => {
    const currentParams = new URLSearchParams(window.location.search);

    // Conditionally update or keep existing
    if (mainTab !== undefined) currentParams.set('mainTab', mainTab);
    if (destinationTab !== undefined) currentParams.set('tab', destinationTab);

    currentParams.set('limit', !limit || isNaN(limit) ? (currentParams.get('limit') || defaultRowsPerPage) : limit);
    currentParams.set('page', !page || isNaN(page) ? (currentParams.get('page') || defaultPage) : page);

    if (query !== undefined && query !== null) currentParams.set('query', query);
    if (archive !== undefined) currentParams.set('archive', archive);
    if (warehouseId !== undefined) currentParams.set('selectedWarehouseId', warehouseId);
    if (siteId !== undefined) currentParams.set('selectedSiteId', siteId);
    if (subTab !== undefined) currentParams.set('subTab', subTab);
    if (client_search !== undefined) currentParams.set('client_search', client_search);

    if (filterSchema && filterList) {
        // Get all possible filter keys from the schema
        const allFilterKeys = new Set();
        Object.values(filterSchema).forEach((filterObject) => {
            const { queryParamKey } = filterObject;
            if (typeof queryParamKey === 'object') {
                // For complex query param keys, add all keys from keyList
                if (queryParamKey.keyList && Array.isArray(queryParamKey.keyList)) {
                    queryParamKey.keyList.forEach(key => filterObject.type !== 'search_filter' ? allFilterKeys.add(key) : null);
                }
            } else if (typeof queryParamKey === 'string') {
                filterObject.type !== 'search_filter' ? allFilterKeys.add(queryParamKey) : null;
            }
        });

        // Remove all existing filter parameters first
        allFilterKeys.forEach(key => {
            currentParams.delete(key);
        });

        // Get current filter query and add only active filters
        const filterQuery = getFilterQueryParams(filterSchema, filterList);
        if (filterQuery) {
            const filterParams = new URLSearchParams(filterQuery);
            for (const [key, value] of filterParams.entries()) {
                currentParams.set(key, value);
            }
        }
    }

    // Preserve additional parameters like text search filters
    Object.entries(additionalParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            currentParams.set(key, value);
        }
    });

    const newSearch = `?${currentParams.toString()}`;
    navigate(`${window.location.pathname}${newSearch}`);
};

